import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useTimerStore } from '@/stores/timerStore';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { AuthModal } from '@/components/AuthModal';
import { cn } from '@/lib/utils';
import { useSharedReport } from '@/contexts/SharedReportContext';
import { useShareModal } from '@/contexts/ShareContext';
import { useAboutModal } from '@/contexts/AboutContext';
import { GlobalShareModal } from '@/components/GlobalShareModal';
import { AboutModal } from '@/components/AboutModal';
import { stripMarkdownLinks } from '@/lib/richText';

interface NavigationLayoutProps {
  children: React.ReactNode;
}

export function NavigationLayout({ children }: NavigationLayoutProps) {
  const { creatorName } = useSharedReport();
  const { openShareModal } = useShareModal();
  const { openAboutModal } = useAboutModal();
  const location = useLocation();
  const pathname = location.pathname;
  const navigate = useNavigate();
  const { isLoggedIn, user, signOut, isPasswordRecovery } = useAuth();
  const {
    isRunning,
    activePieceName,
    pieceTimeRemaining,
    isPiecePaused,
    activePieceBannerDismissed,
    dismissPieceBanner,
    togglePausePiece,
    timeRemaining,
    mode,
    isPracticeComplete,
    isPieceOvertime,
    pieceOvertimeRunning,
    startPieceOvertime,
    stopPieceOvertime,
    startTimer,
    pauseTimer,
    skipTimer,
  } = useTimerStore();

  const isReportPath = pathname.startsWith('/report') || pathname.startsWith('/r/');

  const isPiecePlaying = (isPieceOvertime || isPracticeComplete)
    ? pieceOvertimeRunning
    : (isRunning && !isPiecePaused);

  const handlePiecePlayPause = () => {
    if (isPieceOvertime || isPracticeComplete) {
      if (pieceOvertimeRunning) {
        stopPieceOvertime();
      } else {
        startPieceOvertime();
      }
    } else {
      if (!isRunning) {
        startTimer();
        if (useTimerStore.getState().isPiecePaused) {
          togglePausePiece();
        }
      } else {
        togglePausePiece();
      }
    }
  };

  const [isSidebarExpanded, setIsSidebarExpanded] = useState(() => {
    if (typeof window !== 'undefined') {
      const isReport = window.location.pathname.startsWith('/report') || window.location.pathname.startsWith('/r/');
      if (isReport) {
        const savedReport = localStorage.getItem('sidebar_expanded_report');
        if (savedReport !== null) {
          return savedReport === 'true';
        }
        return false; // Collapsed by default on shared report
      }
      const isSmallScreen = window.innerWidth < 1024;
      if (isSmallScreen) {
        const savedSmall = localStorage.getItem('sidebar_expanded_small');
        if (savedSmall !== null) {
          return savedSmall === 'true';
        }
        return false; // Collapsed by default on small screens (iPad, tablet, mobile)
      }
      const saved = localStorage.getItem('sidebar_expanded');
      if (saved !== null) {
        return saved === 'true';
      }
      return true;
    }
    return true;
  });

  const [authModalOpen, setAuthModalOpen] = useState(false);
  const [authInitialMode, setAuthInitialMode] = useState<'signin' | 'signup'>('signin');

  const [isUser, setIsUser] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const mainRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    const handleScroll = () => {
      const windowY = typeof window !== 'undefined' ? (window.scrollY || document.documentElement.scrollTop || 0) : 0;
      const mainY = mainRef.current ? mainRef.current.scrollTop : 0;
      setIsScrolled(windowY > 4 || mainY > 4);
    };

    const mainEl = mainRef.current;
    window.addEventListener('scroll', handleScroll, { passive: true });
    if (mainEl) {
      mainEl.addEventListener('scroll', handleScroll, { passive: true });
    }

    handleScroll();

    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (mainEl) {
        mainEl.removeEventListener('scroll', handleScroll);
      }
    };
  }, [pathname]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (isLoggedIn) {
      setIsUser(true);
      return;
    }
    const logs = localStorage.getItem('practice-timer-logs');
    if (logs && logs !== '[]') {
      setIsUser(true);
      return;
    }
    const plan = localStorage.getItem('practice-timer-plan');
    if (plan && plan !== '[]' && !plan.includes('Work session 1')) {
      setIsUser(true);
      return;
    }
    setIsUser(false);
  }, [isLoggedIn]);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      if (isReportPath) {
        const savedReport = localStorage.getItem('sidebar_expanded_report');
        if (savedReport === null) {
          setIsSidebarExpanded(false);
        }
      }
    }
  }, [isReportPath]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const mediaQuery = window.matchMedia('(min-width: 1024px)');
    const handleMediaChange = (e: MediaQueryListEvent) => {
      if (isReportPath) return;
      if (e.matches) {
        const saved = localStorage.getItem('sidebar_expanded');
        setIsSidebarExpanded(saved !== null ? saved === 'true' : true);
      } else {
        const savedSmall = localStorage.getItem('sidebar_expanded_small');
        setIsSidebarExpanded(savedSmall !== null ? savedSmall === 'true' : false);
      }
    };
    mediaQuery.addEventListener('change', handleMediaChange);
    return () => mediaQuery.removeEventListener('change', handleMediaChange);
  }, [isReportPath]);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      if (isReportPath) {
        localStorage.setItem('sidebar_expanded_report', String(isSidebarExpanded));
      } else if (window.innerWidth < 1024) {
        localStorage.setItem('sidebar_expanded_small', String(isSidebarExpanded));
      } else {
        localStorage.setItem('sidebar_expanded', String(isSidebarExpanded));
      }
    }
  }, [isSidebarExpanded, isReportPath]);

  if (isPasswordRecovery || pathname === '/reset-password') {
    return (
      <div className="min-h-screen w-full bg-slate-950 flex items-center justify-center p-4">
        <div className={cn(
          "w-full bg-slate-900 border border-white/10 rounded-3xl p-6 sm:p-8",
          "max-w-md"
        )}>
          {children}
        </div>
      </div>
    );
  }

  const toggleSidebar = () => {
    setIsSidebarExpanded(!isSidebarExpanded);
  };

  const handleSignOut = async () => {
    await signOut();
    navigate('/');
  };

  const openSignIn = () => {
    setAuthInitialMode('signin');
    setAuthModalOpen(true);
  };

  // Define navigation destinations
  const navItems = [
    { path: '/', label: 'Timer', icon: 'timer' },
    { path: '/practice-plan', label: 'Practice Plan', icon: 'assignment' },
    { path: '/lesson-plan', label: 'Lesson Plan', icon: 'school' },
    { path: '/repertoire', label: 'Repertoire', icon: 'library_music' },
    { path: '/practice-log', label: 'Practice Log', icon: 'history' },
  ];

  // Detect if on sub-path (like detail screens) for rendering Top Bar back arrow
  const isRepertoireDetail = pathname.startsWith('/repertoire/') && pathname !== '/repertoire';
  const isReportDetail = pathname.startsWith('/report/') || pathname.startsWith('/r/');
  const isReportSubPage = isReportDetail && pathname.includes('/piece/');
  const isSubPage = isRepertoireDetail || isReportSubPage;

  // Determine page title
  let pageTitle = 'Practice Mate';
  if (pathname === '/') {
    pageTitle = 'Timer';
  } else if (pathname === '/practice-plan') {
    pageTitle = 'Practice Plan';
  } else if (pathname === '/lesson-plan') {
    pageTitle = 'Lesson Plan';
  } else if (pathname === '/repertoire') {
    pageTitle = 'Repertoire';
  } else if (isRepertoireDetail || isReportSubPage) {
    pageTitle = 'Piece Details';
  } else if (pathname === '/practice-log') {
    pageTitle = 'Practice Log';
  } else if (pathname === '/settings') {
    pageTitle = 'Settings';
  } else if (isReportDetail) {
    pageTitle = 'Practice Report';
  }

  // Handle back navigation based on page context
  const handleBackNavigation = () => {
    if (isRepertoireDetail) {
      navigate('/repertoire');
    } else if (isReportSubPage) {
      const matchReportToken = pathname.match(/^\/report\/([^\/]+)/);
      const matchReportId = pathname.match(/^\/r\/([^\/]+)/);
      const searchParams = new URLSearchParams(location.search);
      const tabHash = searchParams.get('tab') === 'lesson' ? '#lesson-plan' : '';
      if (matchReportId) {
        navigate(`/r/${matchReportId[1]}${tabHash}`);
      } else if (matchReportToken) {
        navigate(`/report/${matchReportToken[1]}${tabHash}`);
      } else if (location.hash) {
        navigate(`/report${location.hash}`);
      } else {
        navigate(-1);
      }
    } else {
      navigate(-1);
    }
  };

  // Helper to check if a navigation item path matches the current path
  const isTabActive = (path: string) => {
    if (path === '/') {
      return pathname === '/';
    }
    return pathname.startsWith(path);
  };

  // Check if page needs standard wider layout
  const isLargePage = pathname !== '/';

  // Format seconds for active piece overlay
  const formatSeconds = (totalSecs: number) => {
    const mins = Math.floor(totalSecs / 60);
    const secs = totalSecs % 60;
    return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
  };

  return (
    <div className="flex min-h-screen bg-transparent text-foreground font-sans antialiased">
      {/* 1. Desktop & Tablet Side Sidebar/Rail */}
      <aside
        className={cn(
          "sticky top-0 h-screen self-start z-30 flex flex-col border-r border-black/5 dark:border-white/10 bg-slate-100/30 dark:bg-transparent backdrop-blur-md transition-all duration-300 shrink-0",
          isSidebarExpanded ? "w-64" : "w-16"
        )}
      >
        {/* Sidebar Header */}
        <div className="h-16 px-2 md:px-4 flex items-center md:gap-3 border-b border-black/5 dark:border-white/10 justify-center md:justify-start">
          <Button
            variant="ghost"
            size="icon"
            className="hidden md:inline-flex h-10 w-10 text-muted-foreground hover:text-foreground"
            onClick={toggleSidebar}
            aria-label={isSidebarExpanded ? "Collapse sidebar" : "Expand sidebar"}
          >
            <span className="material-icons text-xl">menu</span>
          </Button>
          <div className="md:hidden flex items-center justify-center h-10 w-10 text-primary">
            <svg className="h-6 w-auto fill-current" viewBox="0 0 46 79">
              <path fillRule="evenodd" clipRule="evenodd" d="M20.7463 39.5L1.33284 67.4349C0.536414 68.5779 0.0956646 69.8593 0.0138686 71.1723C-0.0679757 72.4759 0.21219 73.8015 0.860683 75.0421C1.50601 76.2763 2.43777 77.265 3.5585 77.9452C4.68857 78.6285 5.99183 79 7.37697 79H38.623C40.0082 79 41.3114 78.6285 42.4415 77.9452C43.5622 77.2651 44.4908 76.2795 45.1393 75.0421C45.7878 73.8015 46.0648 72.4759 45.9861 71.1723C45.9043 69.8593 45.4604 68.581 44.6672 67.4349L25.2537 39.5L44.6672 11.5651C45.4636 10.4221 45.9043 9.14066 45.9861 7.82767C46.068 6.5241 45.7878 5.19853 45.1393 3.95792C44.494 2.72368 43.5622 1.73496 42.4415 1.05481C41.3114 0.371548 40.0082 0 38.623 0H7.37697C5.99183 0 4.68865 0.371548 3.5585 1.05481C2.43785 1.73492 1.50917 2.72046 0.860683 3.95792C0.212206 5.19853 -0.0647845 6.5241 0.0138686 7.82767C0.095713 9.14066 0.539573 10.419 1.33284 11.5651L20.7463 39.5Z" />
            </svg>
          </div>
          {isSidebarExpanded && (
            <span className="hidden md:inline font-bold text-lg tracking-tight text-primary">
              Practice Mate
            </span>
          )}
        </div>

        {/* Sidebar Navigation Links */}
        <nav className="flex-1 py-4 px-3 space-y-1">
          {isReportPath ? (
            <div className="space-y-2">
              {/* Desktop/Expanded card */}
              <div className={cn(
                "p-3 rounded-2xl bg-primary/5 border border-primary/10 text-center md:text-left transition-all duration-300",
                isSidebarExpanded ? "block" : "hidden md:hidden"
              )}>
                <div className="flex items-center gap-2 text-primary mb-3 justify-center md:justify-start">
                  <span className="material-icons text-lg">share</span>
                  <span className="font-bold text-xs uppercase tracking-wider">Shared View</span>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full justify-center text-xs border-primary/20 text-primary hover:bg-primary/10 rounded-xl"
                  onClick={() => navigate('/')}
                >
                  <span className="material-icons text-sm mr-1">{isUser ? "arrow_back" : "bolt"}</span>
                  {isUser ? "My Workspace" : "Try Practice Mate"}
                </Button>
              </div>

              {/* Collapsed/Icon-only Button */}
              <div className={cn(
                "flex justify-center",
                isSidebarExpanded ? "md:hidden" : "block"
              )}>
                <button
                  onClick={() => navigate('/')}
                  className="flex h-12 w-12 items-center justify-center rounded-2xl text-muted-foreground hover:bg-black/5 dark:hover:bg-white/5 hover:text-foreground transition-all duration-200 relative group"
                  aria-label={isUser ? "Go to My Workspace" : "Try Practice Mate"}
                >
                  <span className="material-icons text-xl">{isUser ? "home" : "bolt"}</span>
                  <span className="absolute left-16 scale-0 bg-slate-900 border border-white/10 text-foreground text-xs py-1 px-2.5 rounded-lg transition-all whitespace-nowrap z-50 group-hover:scale-100">
                    {isUser ? "Go to My Workspace" : "Try Practice Mate"}
                  </span>
                </button>
              </div>
            </div>
          ) : (
            navItems.map((item) => {
              const active = isTabActive(item.path);
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={cn(
                    "flex items-center gap-4 px-2 md:px-3 py-3 rounded-2xl transition-all duration-200 group relative justify-center md:justify-start",
                    active
                      ? "bg-primary/10 text-primary font-medium"
                      : "text-muted-foreground hover:bg-black/5 dark:hover:bg-white/5 hover:text-foreground"
                  )}
                >
                  <div className="flex items-center justify-center h-6 w-6">
                    <span className={cn(
                      "material-icons text-xl",
                      active ? "text-primary" : "text-muted-foreground group-hover:text-foreground"
                    )}>
                      {item.icon}
                    </span>
                  </div>
                  <span className={cn(
                    "text-sm truncate transition-all duration-200",
                    isSidebarExpanded ? "hidden md:inline" : "hidden"
                  )}>
                    {item.label}
                  </span>
                  <span className={cn(
                    "absolute left-16 scale-0 bg-slate-900 border border-white/10 text-foreground text-xs py-1 px-2.5 rounded-lg transition-all whitespace-nowrap z-50",
                    isSidebarExpanded ? "max-md:group-hover:scale-100" : "group-hover:scale-100"
                  )}>
                    {item.label}
                  </span>
                </Link>
              );
            })
          )}
        </nav>

        {/* Sidebar Footer: About & Practice Lab Suite */}
        <div className="p-3 border-t border-black/5 dark:border-white/10 mt-auto space-y-1">
          {isSidebarExpanded ? (
            <div className="space-y-1">
              <button
                onClick={() => openAboutModal('about')}
                className="w-full flex items-center gap-3 px-3 py-2 rounded-xl text-xs text-muted-foreground hover:text-foreground hover:bg-black/5 dark:hover:bg-white/5 transition-all text-left"
              >
                <span className="material-icons text-base text-primary">info</span>
                <span className="font-medium truncate">About Practice Mate</span>
              </button>
              <a
                href="https://practice-lab.net"
                target="_blank"
                rel="noopener noreferrer"
                className="w-full flex items-center justify-between px-3 py-1.5 rounded-xl text-[11px] text-muted-foreground/70 hover:text-foreground hover:bg-black/5 dark:hover:bg-white/5 transition-all group"
              >
                <span className="truncate">Practice Lab Suite</span>
                <span className="material-icons text-xs group-hover:translate-x-0.5 transition-transform">open_in_new</span>
              </a>
            </div>
          ) : (
            <div className="flex flex-col items-center gap-1">
              <button
                onClick={() => openAboutModal('about')}
                className="flex h-10 w-10 items-center justify-center rounded-xl text-muted-foreground hover:bg-black/5 dark:hover:bg-white/5 hover:text-foreground transition-all duration-200 relative group"
                aria-label="About Practice Mate"
              >
                <span className="material-icons text-lg">info</span>
                <span className="absolute left-16 scale-0 bg-slate-900 border border-white/10 text-foreground text-xs py-1 px-2.5 rounded-lg transition-all whitespace-nowrap z-50 group-hover:scale-100">
                  About Practice Mate
                </span>
              </button>
            </div>
          )}
        </div>
      </aside>

      {/* 2. Main Container (Top App Bar + Page Card View) */}
      <div className="flex-1 flex flex-col min-w-0 min-h-screen pb-0">
        {/* Top App Bar */}
        <header
          className={cn(
            "sticky top-0 z-20 h-16 px-4 flex items-center justify-between transition-all duration-200",
            "after:content-[''] after:absolute after:top-full after:left-0 after:right-0 after:h-4 after:pointer-events-none after:transition-opacity after:duration-200",
            isScrolled
              ? "bg-slate-50/85 dark:bg-[#111827]/85 backdrop-blur-md border-b border-black/[0.04] dark:border-white/[0.06] shadow-sm after:bg-gradient-to-b after:from-slate-50/85 dark:after:from-[#111827]/85 after:to-transparent after:opacity-100"
              : "bg-transparent border-b border-transparent after:opacity-0"
          )}
        >
          <div className="flex items-center gap-2">
            {isSubPage && (
              <Button
                variant="ghost"
                size="icon"
                className="h-10 w-10 text-muted-foreground hover:text-foreground mr-1"
                onClick={handleBackNavigation}
                aria-label="Go back"
              >
                <span className="material-icons text-xl">arrow_back</span>
              </Button>
            )}
            <h1 className="text-xl font-bold tracking-tight text-foreground text-ellipsis overflow-hidden whitespace-nowrap max-w-[200px] sm:max-w-none">
              {pageTitle}
            </h1>
          </div>

          <div className="flex items-center gap-3">
            {/* Active Piece Status Banner */}
            {!isReportPath && activePieceName && !activePieceBannerDismissed && (
              <div className="hidden sm:flex items-center gap-3 bg-slate-100/80 dark:bg-slate-900/80 border border-black/5 dark:border-white/5 rounded-full py-1.5 pl-4 pr-2 text-sm">
                <span className="text-muted-foreground font-medium truncate max-w-[150px]">
                  {stripMarkdownLinks(activePieceName)}
                </span>
                <span className="text-primary font-mono font-bold text-base">
                  {formatSeconds(pieceTimeRemaining)}
                </span>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 w-7 p-0 hover:bg-black/5 dark:hover:bg-white/10 rounded-full text-muted-foreground hover:text-foreground transition-colors focus-visible:ring-0 focus-visible:ring-offset-0 focus:outline-none"
                  onPointerDown={(e) => e.preventDefault()}
                  onKeyDown={(e) => {
                    if (e.key === ' ' || e.code === 'Space') e.preventDefault();
                  }}
                  onClick={(e) => {
                    e.currentTarget.blur();
                    handlePiecePlayPause();
                  }}
                  title={isPiecePlaying ? 'Pause piece timer' : 'Resume piece timer'}
                  aria-label={isPiecePlaying ? 'Pause piece timer' : 'Resume piece timer'}
                >
                  <span className="material-icons text-sm">{isPiecePlaying ? 'pause' : 'play_arrow'}</span>
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 w-7 p-0 hover:bg-black/5 dark:hover:bg-white/10 rounded-full text-muted-foreground hover:text-foreground transition-colors focus-visible:ring-0 focus-visible:ring-offset-0 focus:outline-none"
                  onPointerDown={(e) => e.preventDefault()}
                  onKeyDown={(e) => {
                    if (e.key === ' ' || e.code === 'Space') e.preventDefault();
                  }}
                  onClick={(e) => {
                    e.currentTarget.blur();
                    dismissPieceBanner();
                  }}
                  title="Dismiss piece banner"
                  aria-label="Dismiss piece banner"
                >
                  <span className="material-icons text-sm">close</span>
                </Button>
              </div>
            )}

            {/* Global Session Timer Widget */}
            {!isReportPath && typeof timeRemaining === 'number' && !isPracticeComplete && (
              <div className="flex items-center gap-3 bg-slate-100 dark:bg-slate-900 border border-black/5 dark:border-white/10 rounded-full py-1.5 pl-4 pr-2 text-sm">
                <span className={cn(
                  "font-bold uppercase tracking-wider text-[11px]",
                  mode === 'break' ? "text-green-400" : "text-red-400"
                )}>
                  {mode === 'break' ? 'Break' : 'Work'}
                </span>
                <span className="font-mono font-bold text-foreground text-base">
                  {formatSeconds(timeRemaining)}
                </span>
                <div className="flex items-center gap-1 border-l border-black/10 dark:border-white/10 pl-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 w-7 p-0 hover:bg-black/5 dark:hover:bg-white/10 rounded-full text-muted-foreground hover:text-foreground transition-colors focus-visible:ring-0 focus-visible:ring-offset-0 focus:outline-none"
                    onPointerDown={(e) => e.preventDefault()}
                    onKeyDown={(e) => {
                      if (e.key === ' ' || e.code === 'Space') e.preventDefault();
                    }}
                    onClick={(e) => {
                      e.currentTarget.blur();
                      if (isRunning || pieceOvertimeRunning) pauseTimer();
                      else startTimer();
                    }}
                    title={isRunning || pieceOvertimeRunning ? 'Pause' : 'Play'}
                  >
                    <span className="material-icons text-base">{isRunning || pieceOvertimeRunning ? 'pause' : 'play_arrow'}</span>
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 w-7 p-0 hover:bg-black/5 dark:hover:bg-white/10 rounded-full text-muted-foreground hover:text-foreground transition-colors focus-visible:ring-0 focus-visible:ring-offset-0 focus:outline-none"
                    onPointerDown={(e) => e.preventDefault()}
                    onKeyDown={(e) => {
                      if (e.key === ' ' || e.code === 'Space') e.preventDefault();
                    }}
                    onClick={(e) => {
                      e.currentTarget.blur();
                      skipTimer();
                    }}
                    title="Skip session"
                  >
                    <span className="material-icons text-base">skip_next</span>
                  </Button>
                </div>
              </div>
            )}

            {/* Auth Dropdown Widget */}
            {isLoggedIn ? (() => {
              const userDisplayName = (user?.user_metadata?.full_name as string | undefined)
                || (user?.user_metadata?.name as string | undefined)
                || user?.email?.split("@")[0]
                || "User";
              const avatarUrl = user?.user_metadata?.avatar_url as string | undefined;
              const userInitials = (() => {
                const name = user?.user_metadata?.full_name || user?.user_metadata?.name;
                if (name) {
                  const parts = name.trim().split(/\s+/);
                  if (parts.length >= 2) return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
                  return name.slice(0, 2).toUpperCase();
                }
                return (user?.email?.slice(0, 2) || "PM").toUpperCase();
              })();

              return (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="ghost"
                      className="h-10 flex items-center gap-2 pl-1.5 pr-2.5 py-1 rounded-full bg-slate-100 dark:bg-slate-900 border border-black/5 dark:border-white/10 hover:bg-black/5 dark:hover:bg-white/5 transition-all select-none active:scale-95 focus-visible:ring-0 focus-visible:ring-offset-0 focus:outline-none"
                      aria-label="User account menu"
                    >
                      {avatarUrl ? (
                        <img
                          src={avatarUrl}
                          alt={userDisplayName}
                          className="w-7 h-7 rounded-full object-cover border border-black/5 dark:border-white/10"
                        />
                      ) : (
                        <div className="w-7 h-7 rounded-full flex items-center justify-center font-bold text-xs bg-primary text-primary-foreground shadow-sm">
                          {userInitials}
                        </div>
                      )}
                      <span className="hidden sm:inline text-xs font-semibold max-w-[120px] truncate text-foreground">
                        {userDisplayName}
                      </span>
                      <span className="material-icons text-sm text-muted-foreground">expand_more</span>
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent
                    align="end"
                    className="w-60 bg-white dark:bg-slate-900 border border-black/5 dark:border-white/10 text-foreground"
                    onCloseAutoFocus={(e) => e.preventDefault()}
                  >
                    <DropdownMenuLabel className="font-normal">
                      <div className="flex flex-col space-y-0.5">
                        <p className="text-sm font-semibold truncate leading-tight text-foreground">{userDisplayName}</p>
                        <p className="text-xs text-muted-foreground truncate leading-normal">{user?.email}</p>
                      </div>
                    </DropdownMenuLabel>
                  <DropdownMenuSeparator className="bg-white/10" />
                  <DropdownMenuItem
                    onClick={() => navigate('/settings?tab=account')}
                    className="focus:bg-white/5 focus:text-foreground cursor-pointer"
                  >
                    <span className="material-icons text-sm mr-2">settings</span>
                    Account Settings
                  </DropdownMenuItem>
                  <DropdownMenuSeparator className="bg-white/10" />
                  <DropdownMenuItem
                    onClick={handleSignOut}
                    className="text-red-400 focus:bg-red-500/10 focus:text-red-400 cursor-pointer"
                  >
                    <span className="material-icons text-sm mr-2">logout</span>
                    Sign Out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              );
            })() : (
              <Button
                variant="ghost"
                size="default"
                className="h-10 px-4 rounded-xl border border-black/10 dark:border-white/10 text-sm font-semibold text-primary hover:bg-black/5 dark:hover:bg-white/5"
                onClick={openSignIn}
              >
                <span className="material-icons text-base mr-2">login</span>
                Sign In
              </Button>
            )}

            {/* Share Report — only available to signed-in users */}
            {!isReportPath && isLoggedIn && (
              <Button
                variant="ghost"
                size="icon"
                className="h-10 w-10 rounded-full text-muted-foreground hover:text-foreground hover:bg-black/5 dark:hover:bg-white/5"
                onClick={openShareModal}
                aria-label="Share Report"
                title="Share Practice Report"
              >
                <span className="material-icons text-xl">share</span>
              </Button>
            )}

            {/* Settings */}
            <Button
              variant="ghost"
              size="icon"
              className="h-10 w-10 rounded-full text-muted-foreground hover:text-foreground hover:bg-black/5 dark:hover:bg-white/5"
              onClick={() => navigate('/settings')}
              aria-label="Settings"
            >
              <span className="material-icons text-xl">settings</span>
            </Button>
          </div>
        </header>

        {/* Dynamic Card Area */}
        <main ref={mainRef} className="flex-1 overflow-y-auto p-1.5 sm:p-2.5 md:p-3.5 lg:p-6 flex flex-col justify-start">
          {isReportPath && (
            <div className="w-full max-w-4xl mx-auto mb-4 lg:mb-6 bg-primary/5 dark:bg-primary/10 border border-primary/20 rounded-2xl px-4 py-3 sm:px-5 sm:py-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3 sm:gap-4 text-sm backdrop-blur-md">
              <div className="flex items-center gap-3 text-foreground">
                <span className="material-icons text-primary text-xl shrink-0 select-none">cloud_queue</span>
                <span className="leading-relaxed">
                  You are viewing {creatorName ? <strong>{creatorName}'s</strong> : "a"} shared report.
                </span>
              </div>
              <Button
                variant="outline"
                size="sm"
                className="shrink-0 border-primary/20 text-primary hover:bg-primary/10 rounded-xl transition-all font-medium py-1.5"
                onClick={() => navigate('/')}
              >
                {isUser ? (
                  <>
                    <span className="material-icons text-sm mr-1">arrow_back</span>
                    Go to My Workspace
                  </>
                ) : (
                  <>
                    <span className="material-icons text-sm mr-1">bolt</span>
                    Try Practice Mate
                  </>
                )}
              </Button>
            </div>
          )}
          <div
            className={cn(
              "w-full mx-auto bg-white/70 dark:bg-slate-900/50 border border-black/5 dark:border-white/10 rounded-xl md:rounded-2xl lg:rounded-3xl p-2.5 sm:p-3.5 md:p-4 lg:p-6 transition-all duration-300",
              isLargePage ? "max-w-4xl" : "max-w-2xl"
            )}
          >
            {children}
          </div>
        </main>
      </div>

      {/* Auth Modal Container */}
      <AuthModal
        isOpen={authModalOpen}
        onClose={() => setAuthModalOpen(false)}
        initialMode={authInitialMode}
      />

      {/* Global Share Modal Container */}
      <GlobalShareModal />

      {/* About & Privacy Modal Container */}
      <AboutModal />
    </div>
  );
}
